/**
 * EventDetail — one AI event, read in a glance and WITHOUT scrolling.
 *
 * The old detail panel was a narrow column: snapshot on top, then every column of the table
 * repeated underneath as an undifferentiated label/value grid. Two things were wrong with it.
 * The image — the reason the record was opened — arrived small, and the three facts an operator
 * actually acts on (WHEN, WHO, IN or OUT) were mixed in with track ids and confidences, so
 * finding them meant reading the whole grid and scrolling past it.
 *
 * So: snapshots take one side at full width, and the facts split in two. `facts` are the few
 * that answer "what happened" and get tiles big enough to read across a desk; `fields` are the
 * remaining evidence, small and dense. Everything is sized to fit one screen at the modal's
 * `full` size — a record you have to scroll is a record you have to read twice.
 *
 * Presentational only, and a slot for the media: the local app resolves snapshots to URLs while
 * the cloud streams each frame through the node tunnel, and that difference must not fork the
 * layout into two implementations of "event detail".
 */
import type { ReactNode } from 'react';

export type EventFactTone = 'default' | 'good' | 'warn' | 'bad';

export interface EventFact {
  key: string;
  label: string;
  value: ReactNode;
  tone?: EventFactTone;
  /** Ids and plate numbers read better monospaced. */
  mono?: boolean;
}

/**
 * What the model called this event — "Face Recognized", "Loitering", "No-Helmet" — Title Cased
 * out of whichever field carries it. Empty when the row has none, which the title treats as
 * "the model's own name is the most specific thing we can say".
 */
export function eventTypeLabel(event: any): string {
  const raw: string = event?.sub_type || event?.event_type || event?.violation_type || '';
  return raw ? raw.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()) : '';
}

/** The shape of the per-model table column definitions both apps already carry. */
export interface EventColumnLike {
  key: string;
  label: string;
  type?: string;
  render?: (value: any, row?: any) => any;
}

export interface EventValueContext {
  /** camera id → display name. */
  cameraName?: (id: string) => string | undefined;
  /** The registered identity behind a face event, if it has one. */
  personName?: (event: any) => string | null | undefined;
}

const TONE_TEXT: Record<EventFactTone, string> = {
  default: 'text-foreground',
  good: 'text-green-400',
  warn: 'text-amber-400',
  bad: 'text-red-400',
};

const TONE_BADGE: Record<EventFactTone, string> = {
  default: 'bg-muted text-foreground',
  good: 'bg-green-500/15 text-green-500',
  warn: 'bg-amber-500/15 text-amber-500',
  bad: 'bg-red-500/15 text-red-500',
};

function Badge({ text, tone = 'default' }: { text: string; tone?: EventFactTone }) {
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-[11px] font-bold uppercase ${TONE_BADGE[tone]}`}>
      {text}
    </span>
  );
}

/**
 * Render ONE column's value for the detail view, with the tone that goes with it.
 *
 * Shares the table's vocabulary (`col.type`) so a value can't say one thing in the row and
 * another in the record — but not the table's sizing: a badge that has to fit a 13px cell is
 * free to be bigger here.
 */
export function renderEventValue(
  col: EventColumnLike,
  event: any,
  ctx: EventValueContext = {},
): { node: ReactNode; tone: EventFactTone; mono?: boolean } {
  const val = event?.[col.key];
  const plain = (node: ReactNode, tone: EventFactTone = 'default', mono?: boolean) => ({ node, tone, mono });

  switch (col.type) {
    case 'camera':
      return plain(ctx.cameraName?.(val) || (typeof val === 'string' ? val.slice(0, 8) : '') || '—');

    case 'face_identity': {
      const name = ctx.personName?.(event);
      return name ? plain(name, 'good') : plain('Unknown', 'warn');
    }
    case 'person_identity':
      return val ? plain(val, 'good') : plain('—');

    case 'face_status':
      return val ? plain(<Badge text="Known" tone="good" />, 'good') : plain(<Badge text="Unknown" tone="warn" />, 'warn');

    case 'inout':
      if (val === 'in') return plain(<Badge text="↓ In" tone="good" />, 'good');
      if (val === 'out') return plain(<Badge text="↑ Out" tone="bad" />, 'bad');
      return plain('—');

    case 'vehicle_direction':
      return val === 'entry'
        ? plain(<Badge text="↓ Entry" tone="good" />, 'good')
        : plain(<Badge text="↑ Exit" tone="warn" />, 'warn');

    case 'person_id': {
      // The registered identity's id, not the AI's per-track detection id, which stops
      // meaning anything once the face is registered.
      const raw = event?.matched_person_id || val;
      return raw ? plain(String(raw), 'default', true) : plain('—');
    }

    case 'plate_number':
      return val ? plain(String(val).toUpperCase(), 'default', true) : plain('—');

    case 'plate_status':
      if (!event?.has_anpr) return plain('No ANPR');
      return plain(
        <Badge text={val || 'detected'} tone={val === 'valid' ? 'good' : val === 'partial' ? 'warn' : 'bad'} />,
        val === 'valid' ? 'good' : val === 'partial' ? 'warn' : 'bad',
      );

    case 'ppe_violation_type': {
      const tone: EventFactTone = String(val || '').toLowerCase().includes('no-') ? 'bad' : 'warn';
      return plain(<Badge text={val || '—'} tone={tone} />, tone);
    }

    case 'theft_type': {
      const tone: EventFactTone = String(val || '').toLowerCase().includes('shoplifting') ? 'bad' : 'warn';
      return plain(<Badge text={val || '—'} tone={tone} />, tone);
    }

    default:
      if (col.render) return plain(col.render(val, event) ?? '—');
      return plain(val == null || val === '' ? '—' : String(val));
  }
}

/**
 * Which columns earn a headline tile, in the order they're read: when it happened, who it was,
 * which way they went, what the model called it, and where. Everything else is evidence.
 *
 * Keyed off the table's own column vocabulary rather than a per-model list, so a model gaining
 * a column gets the right treatment without another place to edit.
 */
function headlineRank(col: EventColumnLike): number {
  if (col.key === 'event_timestamp') return 0;
  switch (col.type) {
    case 'face_identity':
    case 'person_identity':
      return 1;
    case 'inout':
    case 'vehicle_direction':
      return 2;
    case 'plate_number':
    case 'ppe_violation_type':
    case 'theft_type':
      return 3;
    case 'camera':
      return 4;
    default:
      return -1;
  }
}

/** Columns that are the table's own furniture rather than facts about the event. */
const NON_FACT_TYPES = ['images', 'face_action'];

/**
 * Split a model's columns into the headline facts and the rest, both already rendered.
 * One implementation for the modal and the routed page, in both apps.
 */
export function splitEventFacts(
  event: any,
  columns: EventColumnLike[],
  ctx: EventValueContext = {},
): { facts: EventFact[]; fields: EventFact[] } {
  const facts: (EventFact & { rank: number })[] = [];
  const fields: EventFact[] = [];

  for (const col of columns) {
    if (col.type && NON_FACT_TYPES.includes(col.type)) continue;
    const { node, tone, mono } = renderEventValue(col, event, ctx);
    const rank = headlineRank(col);
    if (rank >= 0) facts.push({ key: col.key, label: col.label, value: node, tone, mono, rank });
    else fields.push({ key: col.key, label: col.label, value: node, tone, mono });
  }

  facts.sort((a, b) => a.rank - b.rank);
  return { facts: facts.map(({ rank, ...f }) => f), fields };
}

export interface EventDetailProps {
  /** The snapshot viewer — each app builds its own, because each loads images differently. */
  media?: ReactNode;
  /**
   * One line under the snapshots. Used to say what the arrow keys do where BOTH the record and
   * the snapshots respond to them — a contract nobody would guess from looking.
   */
  hint?: ReactNode;
  /** Shown large: the few facts the operator acts on. */
  facts: EventFact[];
  /** Shown small and dense: the supporting evidence. */
  fields: EventFact[];
  /** Buttons that act on the event (register this face, …). */
  actions?: ReactNode;
  /**
   * A standing statement about the record rather than a fact of it — "matched against the
   * registered face vector set". Sits in the footer bar opposite the actions, where it says
   * how the record was arrived at without competing with the facts for the reader's eye.
   */
  note?: ReactNode;
  className?: string;
}

export default function EventDetail({ media, hint, facts, fields, actions, note, className = '' }: EventDetailProps) {
  return (
    <div className={`flex min-h-full flex-col ${className}`}>
      <div className="grid flex-1 gap-6 p-5 xl:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
        {/* Snapshots. First in the DOM as well as on screen: on a narrow window the columns
            stack, and the image is still what the record is about. */}
        {media && (
          <div className="min-w-0">
            {media}
            {hint && <p className="mt-2.5 text-[11px] leading-relaxed text-muted-foreground">{hint}</p>}
          </div>
        )}

        <div className="flex min-w-0 flex-col gap-5">
          {/* The headline facts, as tiles. Filled rather than outlined: at two columns a border
              around each drew a grid the eye has to read past to reach the values inside it. */}
          {facts.length > 0 && (
            <div className="grid grid-cols-2 gap-2.5">
              {facts.map((f) => (
                <div key={f.key} className="rounded-xl bg-muted/60 px-3.5 py-2.5">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{f.label}</div>
                  <div
                    className={`mt-1 text-[16px] font-semibold leading-tight ${TONE_TEXT[f.tone || 'default']} ${
                      f.mono ? 'break-all font-mono text-[13px]' : ''
                    }`}
                  >
                    {f.value}
                  </div>
                </div>
              ))}
            </div>
          )}

          {fields.length > 0 && (
            <div>
              <h3 className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">Details</h3>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                {fields.map((f) => (
                  <div key={f.key} className="flex min-w-0 flex-col gap-0.5">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{f.label}</span>
                    <span
                      className={`truncate text-[14px] font-medium ${TONE_TEXT[f.tone || 'default']} ${
                        f.mono ? 'font-mono text-[13px]' : ''
                      }`}
                      title={typeof f.value === 'string' ? f.value : undefined}
                    >
                      {f.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* One bar across the whole record, not a strip inside the right-hand column: what acts on
          the event acts on all of it, and stuck at the bottom of the scroll it stays reachable
          in a record that has been scrolled past its own actions. */}
      {(actions || note) && (
        <div className="sticky bottom-0 z-10 flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-border bg-card/95 px-5 py-3 backdrop-blur-sm">
          <p className="min-w-0 text-[12px] text-muted-foreground">{note}</p>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
      )}
    </div>
  );
}
